import { NgIf } from "@angular/common";
import { HttpEventType, HttpResponse } from "@angular/common/http";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HotToastService } from "@ngneat/hot-toast";
import { environment } from "projects/assist/src/environments/environment";
import { catchError, EMPTY, filter, throwError } from "rxjs";
import { ModalComponent } from "../../../components/layout/modal/modal.component";
import { FormControlElement } from "../../models/form-control-element";
import { MindeeReceipt } from "../../services/mindee";
import { MindeeService } from "../../services/mindee.service";
import { UploadService } from "../../services/upload.service";
import { HorizontalWrapperComponent } from "../horizontal-wrapper/horizontal-wrapper.component";
import { ImageModalComponent } from "../image-modal/image-modal.component";
import { ValidatorComponent } from "../validator/validator.component";
import { DndDirective } from "./dnd.directive";

@Component({
  selector: "app-file-upload",
  templateUrl: "./file-upload.component.html",
  styleUrls: ["./file-upload.component.scss"],
  standalone: true,
  imports: [
    HorizontalWrapperComponent,
    NgIf,
    ReactiveFormsModule,
    DndDirective,
    ValidatorComponent,
    ModalComponent,
  ],
})
export class FileUploadComponent extends FormControlElement implements OnInit {
  @Input() alignRight: boolean = false; // het label rechts uitlijnen
  @Input() noCols: boolean = false;

  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() folder: string = ""; // in welke folder moeten de bestanden opgeladen worden.
  @Input() dropzone: boolean = false; // dropzone of file input
  @Input() instantUpload: boolean = true; // uploaden bij het selecteren van een bestand
  @Input() mindee: boolean = false; // mindee ocr
  @Output() onOCR = new EventEmitter<MindeeReceipt>();
  @Input() targetControl!: FormControl; // de control waar de url van het bestand moet opgeslagen worden
  @Input() mijnAssist: boolean = false; // mijn assist
  @Input() orgKey: string = ""; // mijn assist

  @Input() readOnly = false;

  @Input() modal: boolean = false; // toon modal met meer info
  @Input() modalTitle: string = "Meer info"; // titel van de modal
  @Input() modalInfo!: HTMLDivElement; //  inhoud van de modal

  @Input() type: "file" | "image" = "file"; // type van bestand (file of image)
  @Input() imageOrientation: "landscape" | "portrait" | "squarish" | undefined = "landscape";
  @Input() imageSearchTerm: string = "";

  @ViewChild("content") content!: ElementRef;

  @Input() secure: boolean = false;

  @Output() uploadFunction = new EventEmitter<FileList>(); //als je niet de standaard uploadfunctie wil gebruiken.
  @Output() afterUpload = new EventEmitter<any>();

  percentCompleted: number = 0;
  uploaded: boolean = true;
  uploadControl: FormControl = new FormControl();
  queue: FileList | null = null;
  url = environment.imageUrl;
  @Input() acceptedFileTypes: string[] = [];

  constructor(
    private uploadService: UploadService,
    private toast: HotToastService,
    private mindeeService: MindeeService,
    private modalService: NgbModal
  ) {
    super();
  }

  ngOnInit(): void {
    // Set acceptedFileTypes based on type if not explicitly provided
    if (this.acceptedFileTypes.length === 0) {
      if (this.type === "image") {
        // Houd dit gelijk met wat de backend aanvaardt (IoHelper.ValidMimeTypes/ValidExtensions):
        // enkel jpg/jpeg/png/gif/bmp. webp/svg werden hier vroeger toegelaten maar door de
        // backend geweigerd (400), wat als onverwachte fout in Sentry belandde.
        this.acceptedFileTypes = ["jpg", "jpeg", "png", "bmp", "gif"];
      } else {
        this.acceptedFileTypes = ["jpg", "jpeg", "png", "bmp", "pdf", "docx", "doc", "xlsx", "xls"];
      }
    }

    if (this.required()) this.uploadControl.setValidators(Validators.required);

    // Activeer extern toegevoegde validators op de gebonden control.
    // Het <input type=file> hangt aan de interne uploadControl, dus this.control
    // gaat niet door Angular's setUpControl en zijn validators worden anders nooit
    // herberekend (een lege verplichte file-vraag blijft dan ten onrechte "valid").
    this.control.updateValueAndValidity();

    if (this.control.value) {
      this.uploaded = true;
      this.percentCompleted = 100;
    }

    if (this.control.disabled) this.uploadControl.disable();

    this.control.valueChanges.subscribe((data) => {
      if (this.required()) this.uploadControl.setValidators(Validators.required);
    });
    this.control.statusChanges.subscribe((data) => {
      if (this.control.disabled) this.uploadControl.disable();
      else this.uploadControl.enable();
    });
  }

  uploadFile(e?: Event) {
    if (e) {
      const element = e.currentTarget as HTMLInputElement;
      var files = element.files;
      this.upload(files);
    }
  }

  upload(files: FileList | null) {
    if (this.uploadFunction && this.uploadFunction.observers.length > 0) {
      if (files) {
        this.uploadFunction.emit(files);
      }
    } else {
      if (files && files.length > 1) this.toast.error("Je kan maar 1 bestand tegelijk uploaden");
      if (files) {
        if (!this.isValidFile(files[0].name)) {
          this.toast.error("Dit bestandstype is niet toegelaten");
          throw new Error("Dit bestandstype is niet toegelaten");
        }

        const formData = new FormData();
        formData.append("file", files[0]);
        this.uploaded = false;
        if (!this.mijnAssist)
          this.uploadService
            .uploadWithProgress(formData, this.folder, null, this.secure)
            .pipe(
              filter(
                (event) =>
                  event.type === HttpEventType.UploadProgress || event instanceof HttpResponse
              ),
              catchError((error) => {
                this.control.patchValue("");
                this.uploaded = true;
                if (error.status === 413) {
                  this.toast.error("Bestand is te groot. Maximaal 25MB");
                  return EMPTY;
                }
                return throwError(() => error);
              })
            )
            .subscribe((event) => {
              if (event.type === HttpEventType.UploadProgress) {
                this.percentCompleted = Math.round((100 * event.loaded) / event.total);
                this.control.patchValue("Bezig met opladen... " + this.percentCompleted + "%");
              } else if (event instanceof HttpResponse) {
                this.control.patchValue(event.body.filePath);
                this.uploaded = true;
                this.afterUpload.emit(event.body);
              }
            });
        else
          this.uploadService.uploadMA(formData, this.orgKey).subscribe((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.percentCompleted = Math.round((100 * event.loaded) / event.total);
              this.control.patchValue("Bezig met opladen... " + this.percentCompleted + "%");
            } else if (event instanceof HttpResponse) {
              this.control.patchValue(event.body.filePath);
              this.uploaded = true;
              this.afterUpload.emit(event.body);
            }
          });
      }
    }
  }

  uploadResolve(files: FileList | null): Promise<any> {
    return new Promise((resolve, reject) => {
      if (files && files.length > 1) {
        this.toast.error("Je kan maar 1 bestand tegelijk uploaden");
        reject(false);
      }
      if (files) {
        if (!this.isValidFile(files[0].name)) {
          this.toast.error("Dit bestandstype is niet toegelaten");
          reject(false);
        }

        const formData = new FormData();
        formData.append("file", files[0]);
        this.uploaded = false;
        if (!this.mijnAssist) {
          this.uploadService
            .uploadWithProgress(formData, this.folder, null, this.secure)
            .pipe(
              filter(
                (event) =>
                  event.type === HttpEventType.UploadProgress || event instanceof HttpResponse
              ),
              catchError((error) => {
                this.reset();
                this.uploaded = true;
                if (error.status === 413) {
                  this.toast.error("Bestand is te groot. Maximaal 25MB");
                  reject(false);
                  return EMPTY;
                }
                reject(false);
                return throwError(() => error);
              })
            )
            .subscribe({
              next: (event) => {
                if (event.type === HttpEventType.UploadProgress) {
                  this.percentCompleted = Math.round((100 * event.loaded) / event.total);
                  this.control.patchValue("Bezig met opladen... " + this.percentCompleted + "%");
                } else if (event instanceof HttpResponse) {
                  this.control.patchValue(event.body.filePath);
                  this.uploaded = true;
                  resolve(true);
                } else {
                  //resolve(false);
                }
              },
            });
        } else {
          this.uploadService.uploadMA(formData, this.orgKey).subscribe({
            next: (event) => {
              if (event.type === HttpEventType.UploadProgress) {
                this.percentCompleted = Math.round((100 * event.loaded) / event.total);
                this.control.patchValue("Bezig met opladen... " + this.percentCompleted + "%");
              } else if (event instanceof HttpResponse) {
                this.control.patchValue(event.body.filePath);
                this.uploaded = true;
                resolve(true);
              }
            },
          });
        }
      } else {
        resolve(true);
      }
    });
  }

  uploadQueue() {
    return this.uploadResolve(this.queue);
  }

  queueFiles(e: Event) {
    const element = e.currentTarget as HTMLInputElement;
    var files = element.files;
    this.setQueue(files);
  }

  setQueue(files: FileList | null) {
    this.queue = files;
    const formData = new FormData();
    if (files) formData.append("file", files[0]);
    if (files && this.mindee) this.getAmountFromFile(files[0]);
    this.percentCompleted = 0;
    if (files) this.control.patchValue(files[0].name);
  }

  reset() {
    this.control.patchValue("");
    this.queue = null;
  }

  getFileName() {
    var split = this.control.value?.split("/");
    let lastPart: string = split ? split[split.length - 1] : "";
    if (lastPart.includes("filename=")) {
      lastPart = lastPart.substring(lastPart.indexOf("filename=") + 9);
      lastPart = lastPart.substring(0, lastPart.indexOf("&"));
    }
    return lastPart;
  }

  getAmountFromFile(file: File) {
    if (this.targetControl) this.targetControl.disable();
    this.mindeeService.makeRequest(file).subscribe((data) => {
      if (this.targetControl) this.targetControl.enable();
      this.onOCR.emit(data);
    });
  }

  isValidFile(name: string) {
    var split = name.split(".");
    var ext = split[split.length - 1];
    return this.acceptedFileTypes.indexOf(ext.toLocaleLowerCase()) > -1;
  }

  getAcceptAttribute(): string {
    if (this.type === "image") {
      return "image/*";
    }
    return this.acceptedFileTypes.map((ext) => `.${ext}`).join(",");
  }

  isImageFile(): boolean {
    if (!this.control.value || !this.uploaded) return false;
    const filename = this.getFileName();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const ext = filename.split(".").pop()?.toLowerCase() || "";
    return imageExtensions.includes(ext);
  }

  isExternalUrl(): boolean {
    if (!this.control.value) return false;
    const value = this.control.value;
    return value.startsWith("http://") || value.startsWith("https://");
  }

  getImageUrl(): string {
    if (!this.control.value) return "";
    if (this.isExternalUrl()) {
      return this.control.value;
    }
    return this.url + this.control.value;
  }

  open(content: any) {
    this.modalService.open(content, {
      ariaLabelledBy: "modal-basic-title",
      centered: true,
      size: "lg",
    });
  }

  openUnsplashModal(): void {
    const modalRef = this.modalService.open(ImageModalComponent, {
      size: "xl",
      centered: true,
    });

    // Pass any initial values to the modal
    modalRef.componentInstance.query = this.imageSearchTerm;
    modalRef.componentInstance.orientation = this.imageOrientation;
    modalRef.componentInstance.perPage = 12;
    modalRef.componentInstance.imageProvider = "pexels";

    // Handle the result
    modalRef.result.then(
      (imageUrl: string) => {
        if (imageUrl) {
          this.control.patchValue(imageUrl);
          this.uploaded = true;
          this.percentCompleted = 100;
          //this.afterUpload.emit({ filePath: imageUrl });
        }
      },
      () => {
        // Modal dismissed
      }
    );
  }
}
